<template>
  <div>
    <wc-dialog
      title='Route Details'
      icon='mdi-directions-fork'
      iconColor='teal'
      :showDialog='showDialog'
      :disableButtons='isLoading || !isFormValid'
      :loading='isLoading'
      @cancel="cancel"
      @save='update'
    >
      <v-alert
        v-if='selectedWOs.length > 0 && !route'
        type='warning'
        border='bottom'
        dense
      >
        Adding {{ selectedWOs.length }} WOs to this route
      </v-alert>

      <v-form :disabled='isLoading' v-model='isFormValid' ref='form'>
        <v-row dense>
          <v-col cols='12' v-if='routeCopy.is_template'>
            <v-textarea
              label='Description of route'
              v-model='routeCopy.description'
              outlined
              hint='Will help field crews decide who should be assigned to each route'
              persistent-hint
            ></v-textarea>
          </v-col>
          <v-col cols='12' md='6'>
            <v-autocomplete
              v-if='!routeCopy.is_template'
              :items='leads'
              item-value='id'
              item-text='name_display'
              label='Route Lead'
              @click:append='clearLead'
              append-icon='mdi-close'
              v-model='routeCopy.lead'
            ></v-autocomplete>
          </v-col>
          <v-col cols='12' md='6'>
            <v-autocomplete
              v-if='!routeCopy.is_template'
              :items='staff'
              item-value='id'
              item-text='name_display'
              label='Copilot'
              @click:append='clearCopilot'
              append-icon='mdi-close'
              v-model='routeCopy.copilot'
            ></v-autocomplete>
          </v-col>
          <template v-for='(copilot, index) in other_copilots'>
            <v-col cols='12' md='6' :key='copilot.id'>
              <v-autocomplete
                v-if='!routeCopy.is_template'
                :items='staff'
                item-value='id'
                item-text='name_display'
                label='Other Copilot'
                @click:append='clearOtherCopilot(index, copilot)'
                v-model='other_copilots[index]'
                append-icon='mdi-close'
                @change='updateCopilots($event, copilot, index)'
              ></v-autocomplete>
            </v-col>
          </template>
          <v-col cols='12' md='6'>
            <v-autocomplete
              v-if='!routeCopy.is_template'
              :items='staff'
              item-value='id'
              item-text='name_display'
              label='Other Copilot'
              @change='addCopilot($event)'
            ></v-autocomplete>
          </v-col>
          <v-col cols='12' md='6'>
            <v-autocomplete
              v-if='!routeCopy.is_template'
              :items='vehicles'
              item-value='id'
              item-text='name'
              label='Vehicle'
              prepend-icon='mdi-car'
              @click:append='clearVehicle'
              append-icon='mdi-close'
              v-model='routeCopy.vehicle'
            ></v-autocomplete>
          </v-col>
          <v-col cols='12' md='6'>
            <wc-datepicker
              v-if='!routeCopy.is_template'
              required
              label='Route date'
              v-model='routeCopy.date'
            ></wc-datepicker>
          </v-col>
        </v-row>
        <v-row v-if='!routeCopy.is_template'>
          <v-col cols='12'>
            Notes to crew:
            <v-textarea outlined auto-grow v-model='routeCopy.notes'></v-textarea>
          </v-col>
        </v-row>
      </v-form>
      <template v-slot:dialog-actions v-if='route'>
        <v-tooltip top :disabled='!anyCompleted'>
          <template v-slot:activator='{ on }'>
            <div v-on='on' class='d-inline-block'>
              <v-btn text :disabled='anyCompleted' color='red' @click='confirmDeleteRoute = true'>Delete Route</v-btn>
            </div>
          </template>
          <span>You cannot delete a route with completed work orders</span>
        </v-tooltip>
      </template>
    </wc-dialog>

    <wc-confirm
      :showDialog='confirmDeleteRoute'
      @cancel='confirmDeleteRoute = false'
      @confirm='deleteRoute'
    >
      Are you sure you want to delete this route?
      All the work orders in this route will be unscheduled.
    </wc-confirm>
  </div>
</template>

<script>
import staffingAPI from '@/modules/staffing/api/staffing';
import assetsAPI from '@/modules/assets/api/vehicles';
import _ from 'lodash';

export default {
  name:'route-details-dialog',
  props: {
    showDialog: {
      required: true
    },
    route: {
      required: false
    },
    date: {
      required: false
    }
  },
  data: () => ({
    isLoading: false,
    isFormValid: false,
    confirmDeleteRoute: false,
    routeCopy: {},
    staff: [],
    vehicles: [],
    other_copilots: []
  }),
  computed: {
    leads () {
      return this.staff.filter(s => s.can_be_lead === true)
    },
    selectedWOs () {
      return this.$store.state.schedule.selectedWOs
    },
    anyCompleted () {
      if (!this.routeCopy.wos) {
        return false
      }
      return _.some(this.route.wos, 'is_complete')
    }
  },
  watch: {
    showDialog () {
      if (this.showDialog) {
        if (this.route) {
          this.routeCopy = _.cloneDeep(this.route)
        } else {
          this.routeCopy = {}
        }
        if (!this.routeCopy.date) {
          this.routeCopy.date = this.date
        }
        this.getData()
      }
    }
  },
  methods: {
    async getData () {
      this.isLoading = true;
      this.staff = await staffingAPI.getProfiles();
      this.vehicles = await assetsAPI.getVehicles();
      this.populateOtherCopilots();
      this.isLoading = false;
    },
    update () {
      this.$emit('update', this.routeCopy);
      this.other_copilots = [];
    },
    cancel () {
      this.$emit('cancel')
    },
    clearLead () {
      this.routeCopy.lead = null
    },
    clearCopilot () {
      this.routeCopy.copilot = null
    },
    clearVehicle () {
      this.routeCopy.vehicle = null
    },
    deleteRoute () {
      this.$emit('deleteRoute', this.route);
      this.confirmDeleteRoute = false;
      this.other_copilots = [];
      this.$emit('cancel');
    },
    addCopilot(event){
      this.other_copilots.push(event);
      this.routeCopy.other_copilots = this.other_copilots;
    },
    updateCopilots(event, copilot, index){
      this.other_copilots[index] = event;
      
      this.routeCopy.other_copilots = this.other_copilots;
      console.log(this.other_copilots);
    },
    clearOtherCopilot(index, copilot){
      this.other_copilots.map(other_copilot => {
        if(other_copilot === copilot){
          const index = this.other_copilots.indexOf(other_copilot);
          this.other_copilots.splice(index, 1);
        }
      });
    },
    populateOtherCopilots(){
      if(this.routeCopy.other_copilots === null || this.routeCopy.other_copilots === undefined){
        return;
      }
      for(let i = 0; i < this.routeCopy.other_copilots.length; i++){
        this.other_copilots.push(this.routeCopy.other_copilots[i].pilot_data.id);
      }
      this.routeCopy.other_copilots = this.other_copilots;
    }
  }
}
</script>
